// src/Pages/AddStaffForm.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactDOM from "react-dom";
import DropDownField from "../../../DropDownField";
import {
  Box,
  Paper,
  Grid,
  TextField,
  Typography,
  Button,
  Chip,
  IconButton,
  Avatar,
  Stack,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormHelperText,
  OutlinedInput,
  Divider,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import InputAdornment from "@mui/material/InputAdornment";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import apiEndpoints from "../../../apiconfig";

const initialFormState = {
  guid: null,
  branch: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  password: "",
  qualification: "",
  yearsOfExperience: "",
  role: "",
  specializations: [],
  assignedClass: "",
  joiningDate: null,
  profilePhoto: null,
  salary: "",
};

const AddStaffForm = ({
  onSubmit,
  mode,
  initialData,
  onClose: externalOnClose,
}) => {
  const { guid } = useParams();
  const navigate = useNavigate();

  // ---- state
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [specializationInput, setSpecializationInput] = useState("");
  const [photoPreview, setPhotoPreview] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    type: "",
    value: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  
  // Track if branch is selected to enable/disable fields
  const [isBranchSelected, setIsBranchSelected] = useState(false);
  // Track if dropdowns are loaded (for edit mode)
  const [dropdownsLoaded, setDropdownsLoaded] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  // dropdowns
  const [qualificationOptions, setQualificationOptions] = useState([]);
  const [specializationOptions, setSpecializationOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [classOptions, setClassOptions] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);

  const onBranchSelect = async (branchGuid) => {
    const selectedBranch = branchGuid || "";
    
    setFormData((prev) => ({ 
      ...prev, 
      branch: selectedBranch,
      // Clear dependent fields when branch changes (only in add mode)
      ...(mode !== "edit" && {
        qualification: "",
        role: "",
        specializations: [],
        assignedClass: ""
      })
    }));
    
    // Reset dependent dropdowns and loading state
    setQualificationOptions([]);
    setRoleOptions([]);
    setSpecializationOptions([]);
    setClassOptions([]);
    setDropdownsLoaded(false);
    
    // Set branch selection state
    setIsBranchSelected(!!selectedBranch);
    
    if (!selectedBranch) return;

    const token = localStorage.getItem("token");
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    try {
      const [qRes, rRes, sRes, cRes] = await Promise.all([
        axios.get(
          `${apiEndpoints.dropdown}?type=qualification&branch_guid=${branchGuid}`,
          { headers }
        ),
        axios.get(
          `${apiEndpoints.dropdown}?type=role&branch_guid=${branchGuid}`,
          { headers }
        ),
        axios.get(
          `${apiEndpoints.dropdown}?type=specialization&branch_guid=${branchGuid}`,
          { headers }
        ),
        axios.get(
          `${apiEndpoints.dropdown}?type=class&branch_guid=${branchGuid}`,
          { headers }
        )
      ]);

      if (qRes.data?.status === "success") {
        setQualificationOptions(qRes.data.data);
      }
      if (rRes.data?.status === "success") {
        setRoleOptions(rRes.data.data);
      }
      if (sRes.data?.status === "success") {
        setSpecializationOptions(sRes.data.data);
      }
      if (cRes.data?.status === "success") {
        setClassOptions(cRes.data.data);
      }
      
      // Mark dropdowns as loaded
      setDropdownsLoaded(true);
      
    } catch (err) {
      console.error("Dropdown fetch error:", err);
      setDropdownsLoaded(true); // Still mark as loaded to avoid infinite blocking
    }
  };
useEffect(() => {
  // Lock scroll
  const originalStyle = window.getComputedStyle(document.body).overflow;
  document.body.style.overflow = "hidden";

  return () => {
    // Restore scroll when modal closes
    document.body.style.overflow = originalStyle;
  };
}, []);

  // fetch dropdowns
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const token = localStorage.getItem("token");

        const res = await axios.get(
          `${apiEndpoints.dropdown}?type=branch&mode=initial`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (res.data?.status === "success") {
          setBranchOptions(res.data.data);
        }
      } catch (err) {
        console.error("Branch load error:", err);
      }
    };

    fetchBranches();
  }, []);

  const getBranchName = (guid, branchList) => {
    if (!guid || !Array.isArray(branchList)) return "";
    const found = branchList.find((b) => b.guid === guid);
    return found ? found.name : "";
  };
  
  // preload edit data
  useEffect(() => {
    if (mode === "edit" && initialData) {
      const editFormData = {
        guid: initialData.guid,
        branch: initialData.branch || "",
        firstName: initialData.firstName || "",
        lastName: initialData.lastName || "",
        email: initialData.email || "",
        phone: initialData.phone || "",
        password: initialData.password || "",
        qualification: initialData.qualification || "",
        yearsOfExperience:
          initialData.yearsOfExperience || initialData.experience || "",
        role: initialData.role || "",
        specializations:
          initialData.specialization || initialData.specializations || [],
        assignedClass: initialData.assignedClass || "",
        joiningDate: initialData.joiningDate || "",
        profilePhoto: initialData.profilePhoto || null,
        salary: initialData.salary || "",
      };

      setFormData(editFormData);

      // Set branch selection state
      setIsBranchSelected(!!initialData.branch);

      // 🔥 IMPORTANT — load dropdown options
      if (initialData.branch) {
        onBranchSelect(initialData.branch);
      }

      if (initialData.profilePhoto) {
        const imgSrc = initialData.profilePhoto.startsWith("data:")
          ? initialData.profilePhoto
          : `data:image/jpeg;base64,${initialData.profilePhoto}`;

        setPhotoPreview(imgSrc);
      }
    }
  }, [mode, initialData]);

  // Check if fields should be enabled (for edit mode)
  const shouldEnableField = (fieldType) => {
    if (!isBranchSelected) return false;
    
    // In edit mode, allow editing even if dropdowns are loading
    if (mode === "edit") {
      return true; // Always enable in edit mode once branch is selected
    }
    
    // In add mode, wait for dropdowns to load
    return dropdownsLoaded;
  };

  // ---- editable dropdown helpers
  const handleEditableDropdown = async (type, value) => {
    // Check if field should be enabled
    if (!shouldEnableField(type)) return;
    
    const trimmed = value.trim();
    if (!trimmed) return;

    let options =
      type === "qualification"
        ? qualificationOptions
        : type === "role"
          ? roleOptions
          : specializationOptions;

    const exists = options.some(
      (opt) => opt.name.toLowerCase() === trimmed.toLowerCase()
    );

    if (exists) {
      setFormData((prev) => ({ ...prev, [type]: trimmed }));
      return;
    }

    // Ask to confirm addition
    setConfirmModal({ open: true, type, value: trimmed });
  };

  const confirmAddNewValue = async () => {
    const { type, value } = confirmModal;
    try {
      axios.post(
        `${apiEndpoints.dropdown}?type=${type}`,
        { name: value },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );
      const updated = await axios.get(
        `${apiEndpoints.dropdown}?type=${type}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );
      const list = updated.data?.data || [];

      if (type === "qualification") setQualificationOptions(list);
      if (type === "role") setRoleOptions(list);
      if (type === "specialization") setSpecializationOptions(list);

      setFormData((prev) => ({ ...prev, [type]: value }));
    } catch (err) {
      console.error("Insert failed", err);
    }

    setConfirmModal({ open: false, type: "", value: "" });
  };

  const cancelAddNewValue = () => {
    setConfirmModal({ open: false, type: "", value: "" });
  };

  const maybeCreateDropdownValue = async (type, name) => {
    if (!name || !isBranchSelected) return;

    const lower = name.trim().toLowerCase();
    let list = [];

    if (type === "qualification") list = qualificationOptions;
    else if (type === "role") list = roleOptions;
    else if (type === "specialization") list = specializationOptions;

    if (list.some((item) => item.name.toLowerCase() === lower)) return;

    try {
      await axios.post(
        `${apiEndpoints.dropdown}?type=${type}`,
        { name },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );
      const res = await axios.get(`${apiEndpoints.dropdown}?type=${type}`);
      const data = res.data?.data || [];

      if (type === "qualification") setQualificationOptions(data);
      else if (type === "role") setRoleOptions(data);
      else if (type === "specialization") setSpecializationOptions(data);
    } catch (error) {
      console.error(`Error creating ${type}:`, error);
    }
  };

  // ---- handlers
  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      joiningDate: date,
    }));
  };

  const handleAssignedClassChange = (classGuid) => {
    // Check if field should be enabled
    if (!shouldEnableField("assignedClass")) return;
    
    setFormData((prev) => ({
      ...prev,
      assignedClass: classGuid,
    }));
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      setFormData((prev) => ({ ...prev, profilePhoto: base64String }));
      setPhotoPreview(base64String);
    };
    reader.readAsDataURL(file);
  };

  const addSpecializationChip = async () => {
    // Check if field should be enabled
    if (!shouldEnableField("specialization")) return;
    
    const value = specializationInput.trim();
    if (!value) return;
    if (formData.specializations.includes(value)) return;

    setFormData((prev) => ({
      ...prev,
      specializations: [...prev.specializations, value],
    }));
    setSpecializationInput("");

    await maybeCreateDropdownValue("specialization", value);
  };

  const removeSpecializationChip = (chipToDelete) => {
    setFormData((prev) => ({
      ...prev,
      specializations: prev.specializations.filter(
        (chip) => chip !== chipToDelete
      ),
    }));
  };

  const handleSpecializationKeyDown = (event) => {
    // Check if field should be enabled
    if (!shouldEnableField("specialization")) return;
    
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      addSpecializationChip();
    }
  };

  // ---- validation
  const validate = () => {
    const newErrors = {};

    if (!formData.branch || !formData.branch.trim())
      newErrors.branch = "Branch is required";
    if (!formData.firstName || !formData.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!formData.lastName || !formData.lastName.trim())
      newErrors.lastName = "Last name is required";

    if (!formData.email || !formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Enter a valid email address";
    }

    if (!formData.phone || !formData.phone.trim())
      newErrors.phone = "Phone number is required";
    else if (formData.phone.replace(/\D/g, "").length < 10)
      newErrors.phone = "Enter a valid phone number";

    if (!formData.password || !formData.password.trim())
      newErrors.password = "Password is required";
    else if (formData.password.length < 6)
      newErrors.password = "Password must be at least 6 characters";

    if (!formData.qualification)
      newErrors.qualification = "Qualification is required";
    if (!formData.yearsOfExperience)
      newErrors.yearsOfExperience = "Experience is required";
    if (!formData.role) newErrors.role = "Role is required";
    if (!formData.specializations || !formData.specializations.length)
      newErrors.specializations = "Add at least one specialization";
    if (!formData.assignedClass)
      newErrors.assignedClass = "Select at least one class";
    if (!formData.joiningDate)
      newErrors.joiningDate = "Joining date is required";
    if (!formData.profilePhoto)
      newErrors.profilePhoto = "Profile photo is required";
    
    // Salary validation
    if (!formData.salary || formData.salary === "") {
      newErrors.salary = "Salary is required";
    } else if (isNaN(formData.salary) || parseFloat(formData.salary) < 0) {
      newErrors.salary = "Salary cannot be negative";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleReset = () => {
    setFormData(initialFormState);
    setSpecializationInput("");
    setErrors({});
    setPhotoPreview(null);
    setIsBranchSelected(false);
    setDropdownsLoaded(false);
    
    // Reset dropdowns
    setQualificationOptions([]);
    setRoleOptions([]);
    setSpecializationOptions([]);
    setClassOptions([]);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validate()) return;

    const payload = {
      guid: formData.guid,
      ...formData,
      specialization: formData.specializations,
      qualification: formData.qualification,
      role: formData.role,
      assigned_class: formData.assignedClass,
      years_of_experience: formData.yearsOfExperience,
      joining_date: formData.joiningDate,
      branch: formData.branch,
      salary: parseFloat(formData.salary),
      first_name: formData.firstName,
      last_name: formData.lastName,
      phone: formData.phone,
    };

    if (typeof onSubmit === "function") onSubmit(payload);

    // default navigation after submit (if you use react-router)
    try {
      navigate("/admin/staff-page");
    } catch (err) {
      // ignore if navigate not available
    }
  };

  const ConfirmAddModal = ({ open, value, onCancel, onConfirm }) => (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>Add New Value?</DialogTitle>
      <DialogContent>
        <Typography>
          Do you want to add "<strong>{value}</strong>" to the system?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="error">
          No
        </Button>
        <Button onClick={onConfirm} variant="contained">
          Yes, Add
        </Button>
      </DialogActions>
    </Dialog>
  );

  const handleClose = () => {
    if (externalOnClose) externalOnClose();
    else {
      try {
        navigate(-1);
      } catch (err) {
        // fallback: do nothing
      }
    }
  };

  // ---- render panel
  return ReactDOM.createPortal(
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ConfirmAddModal
        open={confirmModal.open}
        value={confirmModal.value}
        onCancel={cancelAddNewValue}
        onConfirm={confirmAddNewValue}
      />

      {/* FULLSCREEN OVERLAY */}
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 6000,
          bgcolor: "rgba(15,23,42,0.45)",
          backdropFilter: "blur(5px)",
          WebkitBackdropFilter: "blur(12px)",
          display: "flex",
          justifyContent: "flex-end",
        }}
        onClick={handleClose}
      >
        {/* Panel */}
        <Box
          sx={{
            width: { xs: "100%", sm: "80%", md: "55%" },
            height: "100vh",
            p: { xs: 2, md: 3 },
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Paper
            elevation={6}
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
              borderRadius: 4,
              p: { xs: 3, md: 4 },
              bgcolor: "rgba(255,255,255,0.96)",
              backdropFilter: "blur(18px)",
              boxShadow:
                "0 18px 45px rgba(15,23,42,0.35), 0 0 0 1px rgba(148,163,184,0.18)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              animation: "slideIn 0.35s ease",
            }}
          >
            <IconButton
              onClick={handleClose}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                bgcolor: "rgba(15,23,42,0.04)",
                "&:hover": { bgcolor: "rgba(15,23,42,0.08)" },
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Header */}
            <Box
              sx={{
                mb: 3,
                pr: 5,
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "flex-start", md: "center" },
                justifyContent: "space-between",
                gap: 1.5,
              }}
            >
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: 0.3,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  👨‍🏫{" "}
                  {mode === "edit"
                    ? "Edit Staff Member"
                    : "Add New Staff Member"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mt: 0.5 }}
                >
                  Fill in the staff details below. Fields marked with * are
                  required.
                </Typography>
              </Box>

              <Chip
                label="Staff Management"
                variant="outlined"
                sx={{
                  borderRadius: "999px",
                  fontSize: 12,
                  fontWeight: 500,
                  borderStyle: "dashed",
                }}
              />
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Form area (scrollable) */}
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ flex: 1, overflowY: "auto", pr: 1 }}
            >
              <Grid container spacing={3} pt={1}>
                {/* Main column */}
                <Grid item xs={12} md={8}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} md={12}>
                      <FormControl fullWidth error={Boolean(errors.branch)}>
                        <DropDownField
                          label="Branch *"
                          value={formData.branch}
                          options={branchOptions}
                          error={Boolean(errors.branch)}
                          onChange={(guid) => {
                            onBranchSelect(guid);
                          }}
                        />
                        <FormHelperText>
                          {!isBranchSelected && "Select a branch to enable other fields"}
                        </FormHelperText>
                      </FormControl>
                    </Grid>

                    {/* First / Last */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="First Name *"
                        placeholder="e.g. Deepika"
                        value={formData.firstName}
                        onChange={handleChange("firstName")}
                        fullWidth
                        size="medium"
                        error={Boolean(errors.firstName)}
                        helperText={errors.firstName}
                        InputProps={{ sx: { borderRadius: 3 } }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Last Name *"
                        placeholder="e.g. R"
                        value={formData.lastName}
                        onChange={handleChange("lastName")}
                        fullWidth
                        size="medium"
                        error={Boolean(errors.lastName)}
                        helperText={errors.lastName}
                        InputProps={{ sx: { borderRadius: 3 } }}
                      />
                    </Grid>

                    {/* Email / Phone */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        type="email"
                        label="Email *"
                        placeholder="name@school.edu"
                        value={formData.email}
                        onChange={handleChange("email")}
                        fullWidth
                        size="medium"
                        error={Boolean(errors.email)}
                        helperText={errors.email}
                        InputProps={{ sx: { borderRadius: 3 } }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Phone *"
                        placeholder="10–12 digit phone number"
                        value={formData.phone}
                        onChange={(e) => {
                          const onlyNumbers = e.target.value.replace(/\D/g, "");
                          if (onlyNumbers.length <= 12) {
                            setFormData((prev) => ({
                              ...prev,
                              phone: onlyNumbers,
                            }));
                          }
                        }}
                        fullWidth
                        size="medium"
                        error={Boolean(errors.phone)}
                        helperText={errors.phone}
                        inputProps={{
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                        }}
                        InputProps={{ sx: { borderRadius: 3 } }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth variant="outlined">
                        <TextField
                          label="Password *"
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter password"
                          value={formData.password}
                          onChange={handleChange("password")}
                          error={Boolean(errors.password)}
                          helperText={errors.password}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={togglePasswordVisibility}
                                  edge="end"
                                >
                                  {showPassword ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                            sx: { borderRadius: 3 },
                          }}
                          fullWidth
                        />
                      </FormControl>
                    </Grid>
                    
                    {/* Qualification */}
                    <Grid item xs={12} md={6}>
                      <FormControl
                        fullWidth
                        error={Boolean(errors.qualification)}
                      >
                        <DropDownField
                          label="Qualification *"
                          value={formData.qualification}
                          options={qualificationOptions.map((q) => ({
                            guid: q.name,
                            name: q.name,
                          }))}
                          error={Boolean(errors.qualification)}
                          onChange={(value) =>
                            setFormData((prev) => ({
                              ...prev,
                              qualification: value,
                            }))
                          }
                          disabled={!shouldEnableField("qualification")}
                        />
                        <FormHelperText>
                          {!shouldEnableField("qualification") && "Select a branch first"}
                          {shouldEnableField("qualification") && mode === "edit" && !dropdownsLoaded && "Loading options..."}
                        </FormHelperText>
                      </FormControl>
                    </Grid>

                    {/* Years of Experience */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        type="number"
                        label="Years of Experience *"
                        placeholder="e.g. 5"
                        value={formData.yearsOfExperience}
                        onChange={handleChange("yearsOfExperience")}
                        fullWidth
                        size="medium"
                        error={Boolean(errors.yearsOfExperience)}
                        helperText={errors.yearsOfExperience}
                        InputProps={{
                          sx: { borderRadius: 3 },
                          inputProps: { min: 0 },
                        }}
                      />
                    </Grid>

                    {/* Role */}
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth error={Boolean(errors.role)}>
                        <DropDownField
                          label="Role *"
                          value={formData.role}
                          options={roleOptions.map((r) => ({
                            guid: r.name,
                            name: r.name,
                          }))}
                          error={Boolean(errors.role)}
                          onChange={(value) =>
                            setFormData((prev) => ({ ...prev, role: value }))
                          }
                          disabled={!shouldEnableField("role")}
                        />
                        <FormHelperText>
                          {!shouldEnableField("role") && "Select a branch first"}
                          {shouldEnableField("role") && mode === "edit" && !dropdownsLoaded && "Loading options..."}
                        </FormHelperText>
                      </FormControl>
                    </Grid>

                    {/* Specializations (chips) */}
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={Boolean(errors.specializations)}
                      >
                        <TextField
                          label="Specializations"
                          placeholder={shouldEnableField("specialization") ? "Type and press Enter to add…" : "Select a branch first"}
                          value={specializationInput}
                          onChange={(e) =>
                            setSpecializationInput(e.target.value)
                          }
                          onKeyDown={handleSpecializationKeyDown}
                          fullWidth
                          size="small"
                          disabled={!shouldEnableField("specialization")}
                          InputProps={{ 
                            sx: { 
                              borderRadius: 3,
                              bgcolor: !shouldEnableField("specialization") ? "rgba(0,0,0,0.04)" : "inherit"
                            } 
                          }}
                        />

                        <Box
                          sx={{
                            mt: 1,
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            border: "1px dashed #ccc",
                            p: 1,
                            borderRadius: 2,
                            minHeight: 45,
                            bgcolor: !shouldEnableField("specialization") ? "rgba(0,0,0,0.04)" : "inherit",
                            opacity: !shouldEnableField("specialization") ? 0.6 : 1
                          }}
                        >
                          {formData.specializations.map((skill) => (
                            <Chip
                              key={skill}
                              label={skill}
                              onDelete={() => removeSpecializationChip(skill)}
                              size="small"
                              disabled={!shouldEnableField("specialization")}
                            />
                          ))}
                        </Box>

                        <FormHelperText>
                          {errors.specializations || (!shouldEnableField("specialization") && "Select a branch first")}
                          {shouldEnableField("specialization") && mode === "edit" && !dropdownsLoaded && " (Loading options...)"}
                        </FormHelperText>
                      </FormControl>
                    </Grid>

                    {/* Assigned Classes */}
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={Boolean(errors.assignedClass)}
                      >
                        <DropDownField
                          label="Assigned Class *"
                          value={formData.assignedClass}
                          options={classOptions}
                          error={Boolean(errors.assignedClass)}
                          onChange={handleAssignedClassChange}
                          disabled={!shouldEnableField("assignedClass")}
                        />

                        <FormHelperText>
                          {errors.assignedClass || (!shouldEnableField("assignedClass") && "Select a branch first")}
                          {shouldEnableField("assignedClass") && mode === "edit" && !dropdownsLoaded && "Loading options..."}
                        </FormHelperText>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={12}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: 600 }}
                      >
                        Joining Date & Salary *
                      </Typography>
                    </Grid>

                    {/* Joining Date */}
                    <Grid item xs={12} md={6}>
                      <FormControl
                        fullWidth
                        error={Boolean(errors.joiningDate)}
                      >
                        <TextField
                          type="date"
                          value={formData.joiningDate || ""}
                          onChange={(e) => handleDateChange(e.target.value)}
                          fullWidth
                          size="medium"
                          InputLabelProps={{ shrink: true }}
                          InputProps={{ sx: { borderRadius: 3 } }}
                          error={Boolean(errors.joiningDate)}
                          helperText={errors.joiningDate}
                        />
                      </FormControl>
                    </Grid>

                    {/* Salary */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        type="number"
                        label="Salary *"
                        placeholder="e.g. 50000.00"
                        value={formData.salary || ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            salary: parseFloat(e.target.value) || "",
                          }))
                        }
                        fullWidth
                        size="medium"
                        error={Boolean(errors.salary)}
                        helperText={errors.salary}
                        InputProps={{
                          sx: { borderRadius: 3 },
                          inputProps: { 
                            step: "0.01", 
                            min: "0",
                            onKeyDown: (e) => {
                              // Prevent negative numbers
                              if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                                e.preventDefault();
                              }
                            }
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Right column (photo + tips) */}
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      borderRadius: 4,
                      p: 2.5,
                      bgcolor: "rgba(15,23,42,0.03)",
                      border: "1px solid",
                      borderColor: "rgba(148,163,184,0.3)",
                      display: "flex",
                      flexDirection: "column",
                      gap: 2.5,
                    }}
                  >
                    <FormControl error={Boolean(errors.profilePhoto)}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: 600 }}
                      >
                        Profile Photo *
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            width: 140,
                            height: 140,
                            borderRadius: "50%",
                            border: "2px dashed rgba(148,163,184,0.6)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "rgba(255,255,255,0.9)",
                            overflow: "hidden",
                          }}
                        >
                          {photoPreview ? (
                            <>
                              <Avatar
                                src={photoPreview}
                                alt="Profile Preview"
                                sx={{ width: "100%", height: "100%" }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setPhotoPreview(null);
                                  setFormData((prev) => ({
                                    ...prev,
                                    profilePhoto: null,
                                  }));
                                }}
                                sx={{
                                  position: "absolute",
                                  top: 4,
                                  right: 4,
                                  bgcolor: "rgba(15,23,42,0.7)",
                                  color: "#fff",
                                  "&:hover": { bgcolor: "rgba(15,23,42,0.9)" },
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <AddPhotoAlternateIcon
                              sx={{
                                fontSize: 40,
                                color: "rgba(148,163,184,0.9)",
                              }}
                            />
                          )}
                        </Box>

                        <Button
                          variant="contained"
                          component="label"
                          sx={{
                            borderRadius: 999,
                            textTransform: "none",
                            px: 3,
                            fontWeight: 500,
                          }}
                        >
                          Upload Photo
                          <input
                            hidden
                            accept="image/*"
                            type="file"
                            onChange={handlePhotoChange}
                          />
                        </Button>

                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", textAlign: "center" }}
                        >
                          Recommended: Square image, at least 400×400px. PNG or
                          JPG only.
                        </Typography>
                      </Box>

                      <FormHelperText>{errors.profilePhoto}</FormHelperText>
                    </FormControl>

                    <Box
                      sx={{
                        borderRadius: 3,
                        p: 1.5,
                        bgcolor: "rgba(79,70,229,0.04)",
                        border: "1px solid rgba(129,140,248,0.4)",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          mb: 0.5,
                          fontSize: 13,
                          color: "primary.main",
                        }}
                      >
                        Tip for Admins
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", lineHeight: 1.6 }}
                      >
                        Assign classes and add specializations carefully. This
                        helps in timetable generation, student mapping, and
                        workload distribution.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {/* Actions */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                justifyContent="flex-end"
                sx={{ mt: 4 }}
              >
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  sx={{ borderRadius: 999, textTransform: "none", px: 3 }}
                >
                  Clear Form
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!isBranchSelected}
                  sx={{
                    borderRadius: 999,
                    textTransform: "none",
                    px: 4,
                    fontWeight: 600,
                    boxShadow:
                      "0 10px 25px rgba(79,70,229,0.35), 0 0 0 1px rgba(129,140,248,0.5)",
                    '&:disabled': {
                      opacity: 0.6,
                    }
                  }}
                >
                  {mode === "edit" ? "Update Staff" : "Save Staff"}
                </Button>
              </Stack>
            </Box>
          </Paper>
        </Box>
      </Box>

      <style>
        {`
          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        `}
      </style>
    </LocalizationProvider>,
    document.body
  );
};

export default AddStaffForm;